import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type {
  AccumulatorReplyMessage,
  ForwardAccumulatorRfsRequest,
} from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulator.model.ts';
import { getForwardAccumulatorRfsId } from '@/store/forwardAccumulator/selectors.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { FxForwardAccumulator } from '@/store/forwardAccumulator/state.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { type IQuoteAbort, isQuoteTraderNotification, isQuoteWithPrice, type QuoteReply } from '@/utils/rfs/type.ts';

export function requestForwardAccumulatorRfs(builder: WebapiEndpointBuilder) {
  return builder.mutation<{ skipCreditCheck: boolean }, ForwardAccumulatorRfsRequest>({
    query: (params) => {
      return {
        url: '/fxAccumulator/rfs/request',
        method: 'POST',
        body: params.body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },
    transformResponse(_baseQueryReturnValue, _meta, arg) {
      return {
        skipCreditCheck: arg.body.fxForwardAccumulatorProduct.skipCreditCheck,
      };
    },
    async onCacheEntryAdded(params, { dispatch, cacheDataLoaded, cacheEntryRemoved, getState }) {
      const { tileId, rfsId } = params;

      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;

      const quoteParams = { tileId, rfsId, dispatch, getState: getState as () => AppState };

      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(quoteParams);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(quoteParams);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      await cacheEntryRemoved;
      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
}

interface QuoteParams {
  tileId: string;
  rfsId: string;
  dispatch: AppDispatch;
  getState: () => AppState;
}

function buildHandleReceiveQuoteReply(quoteParams: QuoteParams) {
  return (reply: QuoteReply<AccumulatorReplyMessage>) => {
    const { dispatch, tileId, rfsId } = quoteParams;

    if (!isQuoteValid(reply, quoteParams)) {
      return;
    }

    if (isQuoteWithPrice(reply)) {
      if (reply.AccumulatorQuoteReply?.RfsId !== rfsId) {
        return;
      }

      dispatch(
        forwardAccumulatorSlice.actions.forwardAccumulatorRfsQuoteReceived({
          tileId,
          message: reply,
        }),
      );
    } else if (isQuoteTraderNotification(reply)) {
      dispatch(
        forwardAccumulatorSlice.actions.forwardAccumulatorTraderNotificationReceived({
          tileId,
          message: reply,
        }),
      );
    }
  };
}

function buildHandleReceiveQuoteAbort(quoteParams: QuoteParams) {
  return (quoteAbort: IQuoteAbort) => {
    const { dispatch, tileId } = quoteParams;

    if (isQuoteValid(quoteAbort, quoteParams)) {
      dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorRfsQuoteAborted({ tileId, reply: quoteAbort }));
    }
  };
}

function isQuoteValid(message: QuoteReply<AccumulatorReplyMessage>, quoteParams: QuoteParams): boolean {
  const { rfsId, tileId, getState } = quoteParams;
  if (message.RequestId !== rfsId) {
    return false;
  }

  const state = getState();
  const forwardAccumulator = state.forwardAccumulator[tileId];
  const forwardAccumulatorRfsId = isDefined(forwardAccumulator)
    ? getForwardAccumulatorRfsId(forwardAccumulator)
    : undefined;

  if (isNotDefined(forwardAccumulatorRfsId)) {
    // rfs was just cancelled
    return false;
  }

  if (message.RequestId !== forwardAccumulatorRfsId) {
    // Old request still lingering (maybe cancel was unsuccessful, or we received a quote while cancelling)
    return false;
  }
  return true;
}

export const createForwardAccumulatorRfsRequestParams = (
  tile: FxForwardAccumulator,
  tileId: string,
  rfsId: string,
  clientId: number,
  signalRConnectionId: string | undefined,
  forceTradable = false,
  skipCreditCheck = false,
): ForwardAccumulatorRfsRequest | undefined => {
  const forwardAccumulator = tile.properties;

  const {
    currencyPair,
    clientsWay,
    expiryDate,
    deliveryDate,
    premiumDate,
    firstFixingDate,
    lastFixingDate,
    hedgeType,
    hedgeAmount,
    hedgePaymentDate,
    spotHedge,
    //ekiTrigger,
    // targetProfitType,
    //target,
    premiumPriceType,
    expiryDateTenor,
    marginCurrency,
    notionalCurrencyIndex,
    notional,
    premiumTypeCurrencyIndex,
    strike,
    calendar,
    fixingReference1,
    fixingReference2,
    fixingFrequency,
    cashSettlementCurrency,
    cutOffMarketPlace,
    strikeComparator,
    leverage,
    step,
    accuType,
    barrier,
    stepDown,
    stepUp,
  } = forwardAccumulator;

  return {
    tileId,
    rfsId,
    body: {
      fxForwardAccumulatorProduct: {
        currencyPair,
        accumulatorType: 'ForwardAccumulator',
        priceType: premiumPriceType,
        ...(hedgeType === 'Live'
          ? {}
          : {
              hedge: hedgeType,
              hedgePrice: spotHedge,
              hedgeAmount,
              hedgePaymentDate: hedgePaymentDate === null ? undefined : new Date(hedgePaymentDate),
            }),
        premiumDate: premiumDate ? new Date(premiumDate) : undefined,
        premiumCurrency: premiumTypeCurrencyIndex === 1 ? 'ccy1' : 'ccy2',
        way: clientsWay,
        notional: notional === null ? undefined : String(notional),
        notionalCurrency: notionalCurrencyIndex,
        leverageRatio: isDefined(leverage) ? String(leverage) : undefined,
        strike: strike === null ? undefined : Number(strike),
        firstFixing: firstFixingDate ? new Date(firstFixingDate) : undefined,
        expiryTenor: expiryDateTenor ?? undefined,
        fixingFrequency: fixingFrequency,
        lastFixing: lastFixingDate ? new Date(lastFixingDate) : undefined,
        accuType,
        expiryDate: expiryDate ? new Date(expiryDate) : undefined,
        deliveryDate: deliveryDate ? new Date(deliveryDate) : undefined,
        fixingReference1: fixingReference1 ?? '',
        fixingReference2: fixingReference2 ?? '',
        cashSettlementCurrency,
        skipCreditCheck,
        calendar,
        cutOffMarketPlace: cutOffMarketPlace ?? '',
        crossCurrency: marginCurrency,
        strikeComparator,
        stepDown: stepDown,
        stepUp: stepUp,
        settlementFrequency: 'Maturity',
        step: step?.toString(),
        barrier: isDefined(barrier)
          ? {
              level: barrier.level ?? 0,
              nature: barrier.nature ?? 'American',
              type: barrier.type === 'DownOut' ? 'DO' : 'UO',
            }
          : null,
        product: 'ForwardAccumulator',
      },
      replyToStream: signalRConnectionId,
      rfsId,
      forceTradable,

      requesterCounterpartyId: clientId,
    },
  };
};

export function getCurrenciesFromCurrencyPair(currencyPair: string | undefined): {
  currency1: string | undefined;
  currency2: string | undefined;
} {
  if (isNotDefined(currencyPair) || isEmpty(currencyPair)) {
    return { currency1: undefined, currency2: undefined };
  }

  const currencies = currencyPair.split('/');

  const [currency1, currency2] = currencies;

  return { currency1, currency2 };
}
